import { useState } from "react";
import { CalendarClock, MailCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { type ExistingBooking, formatMeeting, meetingTitle, SchedulerError, schedulerApi } from "../services/schedulerApi";

const primary =
  "inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 font-semibold text-text-inverse hover:bg-primaryHover disabled:cursor-not-allowed disabled:opacity-60";

// Shown instead of new times when a meeting is already booked. The public form cannot prove who is typing, so
// the reschedule/cancel link is only ever emailed to the address that made the booking (or, on a SOW review
// link, which does prove who the visitor is, shown directly).
const ExistingMeeting: React.FC<{ existing: ExistingBooking; email?: string; company?: string }> = ({
  existing, email, company,
}) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailLink = async () => {
    if (!email || sending) return;
    setSending(true);
    setError(null);
    try {
      await schedulerApi.emailManageLink({ email, company: company ?? "" });
      setSent(true);
    } catch (err) {
      setError(err instanceof SchedulerError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const emailButton = email && (
    sent ? (
      <p role="status" className="inline-flex items-center gap-2 text-green-700 font-medium">
        <MailCheck className="w-5 h-5" aria-hidden="true" /> Sent. Check your inbox for the reschedule link.
      </p>
    ) : (
      <button type="button" className={primary} disabled={sending} onClick={() => void emailLink()}>
        {sending ? "Sending..." : "Email me my reschedule link"}
      </button>
    )
  );

  return (
    <div className="text-center py-6">
      <CalendarClock className="w-14 h-14 text-primary mx-auto mb-4" aria-hidden="true" />
      {error && <p role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      {existing.same_person ? (
        <>
          <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">You already have a meeting with us</h2>
          <p className="text-text-secondary mb-6">
            {meetingTitle(existing)} with {existing.lead.name}
            <br />
            <strong className="text-text-primary">{formatMeeting(existing.starts_at, existing.timezone)}</strong>
          </p>
          {existing.manage_token ? (
            <Link
              to={`/book/manage?token=${encodeURIComponent(existing.manage_token)}`}
              className={primary}
            >
              Reschedule or cancel
            </Link>
          ) : (
            <>
              <p className="text-text-secondary mb-6">
                You can only have one upcoming meeting at a time. To move or cancel it, we&apos;ll email the link to
                the address you booked with, or use the <strong>Reschedule or cancel</strong> link in your calendar
                invite.
              </p>
              {emailButton}
            </>
          )}
        </>
      ) : (
        <>
          <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
            {existing.company} already has a meeting with us
          </h2>
          <p className="text-text-secondary mb-6">
            Someone at your company has an upcoming {meetingTitle(existing)}. We can email the reschedule link
            to the person who booked it, or email{" "}
            <a className="text-primary underline" href="mailto:hello@contextral.com">hello@contextral.com</a>{" "}
            and we&apos;ll add you to it.
          </p>
          {email && (sent ? (
            <p role="status" className="inline-flex items-center gap-2 text-green-700 font-medium">
              <MailCheck className="w-5 h-5" aria-hidden="true" /> Sent to the person who booked it.
            </p>
          ) : (
            <button type="button" className={primary} disabled={sending} onClick={() => void emailLink()}>
              {sending ? "Sending..." : "Email the link to the person who booked"}
            </button>
          ))}
        </>
      )}
    </div>
  );
};

export default ExistingMeeting;
